/**
 * @fileoverview The one question asked wherever a plant is taken out of the
 * garden: archive it, or delete it for good.
 *
 * Archiving keeps the photos, notes and history; Archived has Restore. A short
 * line says it was done and goes by itself, with no Undo: once a plant is
 * removed, it is removed (owner, 2026-10-20, B26). Deleting is the other door:
 * it leads to the same type-DELETE step the Archived page uses and takes the
 * photos and notes with it. Every place that archives a plant opens this
 * dialog, so there is exactly one flow and one set of words.
 *
 * It also asks the question for several plants at once (a bed's "Clear the
 * bed": the ticked plants), listing their names; the delete then archives and
 * deletes each plant in turn, and a plant that fails is named in the dialog
 * while the rest stay done.
 *
 * For a plant with twins it also offers "Merge into…" (B136: the card's "..." menu is gone, so Merge lives here, beside Archive):
 * onMerge closes the dialog and hands the plant to the merge flow.
 *
 * Where: PlantFormDialog (its Archive… button; the plant card's Archive and Merge moved inside Edit) and LocationGrid (the bed's
 * ClearBedPanel, many plants).
 * API: none of its own; the caller's archive prop archives (PATCH
 * /plants/:id/status through PlantsContext), and deletePlantForever does
 * DELETE /plants/:id { confirm: 'DELETE' }.
 * Settings: none of its own; the line after an archive keeps the toast's
 * usual time (TOAST_DURATION_MS, config/tunables/ui).
 * Words (en/es): plants.form.archiveOrDeleteTitle, remove, removeConfirm,
 * removeFailed, deleteForeverEllipsis, deleteHint, cancel, back,
 * removed, deleted, manyList; plants.merge.menu (Merge into…); plants.archive.delete.*;
 * for several plants archiveOrDeleteTitleMany, removeConfirmMany,
 * deleteManyForever, deleteHintMany, deletedMany.
 * Watch out: a plant still in the garden is archived first and then deleted
 * (the server deletes only what is archived): one decision, two steps.
 *
 * @module ArchiveOrDeleteDialog
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { toast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { deletePlantForever } from '@/shared/api/plantCatalogue';
import type { Plant } from '@/shared/types/plant';
import { cn } from '@/lib/utils';
import { errorSentence } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { DeletePlantConfirm } from './DeletePlantDialog';
import { plantName } from '@/shared/lib/plantName';

/** What the dialog needs: the plant (null when shut), what goes with it, and how to archive. */
export interface ArchiveOrDeleteDialogProps {
  /** The plant being asked about; the dialog is open while this is set. */
  plant: Plant | null;
  /** Asks to close (cancel, Escape, a tap outside); ignored while a step is on its way. */
  onClose: () => void;
  /** Archives the plant by id; a rejection is said in the dialog. */
  archive: (plantObjId: number) => Promise<void>;
  /** How many photos a delete takes with it, said in the warning. */
  photos: number;
  /** How many notes a delete takes with it (the photos' own included). */
  notes: number;
  /** Called once the plant has been archived or deleted, after the dialog asks to close. */
  onDone?: () => void;
  /**
   * Several plants instead of one: when set (and plant is null) the dialog
   * asks about all of them, listing their names.
   */
  many?: ManyPlants | null;
  /** Merge into… for a plant with twins (B136): called with the plant after the dialog closes. Omitted where merging is not offered. */
  onMerge?: (plant: Plant) => void;
}

/** Several plants asked about together, and how to archive them. */
export interface ManyPlants {
  /** The plants, by id and the name shown in the list. */
  plants: Array<{ plantObjId: number; name: string }>;
  /** Archives them all as the caller wants it said (its own toast, with no Undo); a rejection is said in the dialog. */
  archiveAll: (plantObjIds: number[]) => Promise<void>;
  /** Archives some quietly, as the first step of a delete (no toast). */
  archiveQuietly: (plantObjIds: number[]) => Promise<void>;
  /** Called after the deletes, to read the beds and lists again. */
  onDeleted?: () => Promise<void> | void;
}

/**
 * "Archive or delete {name}?": Archive (Archived can restore it), Delete for good… (leads to
 * the type-DELETE step), and the foot: Cancel on the left, Archive on the right (an ActionRow, B111; Button loading while it archives). From the bottom on a phone, a small box in
 * the middle from sm up.
 *
 * @param props - ArchiveOrDeleteDialogProps.
 */
export const ArchiveOrDeleteDialog: React.FC<ArchiveOrDeleteDialogProps> = ({
  plant, onClose, archive, photos, notes, onDone, many = null, onMerge,
}) => {
  /** The app's words, in the gardener's language. */
  const { reactTranslate: t } = useTranslation();
  /** The garden's plants, to sum the photos and notes several plants take with them. */
  const { plants: gardenList } = usePlantsContext();
  /** The query cache, to read the garden and the archive again after a delete. */
  const queryClient = useQueryClient();
  /** Which step is showing: the two choices, or the type-DELETE question. */
  const [step, setStep] = React.useState<'choose' | 'delete'>('choose');
  /** True while archiving is on its way. */
  const [archiving, setArchiving] = React.useState(false);
  /** True while deleting is on its way. */
  const [deleting, setDeleting] = React.useState(false);
  /** Why the last attempt failed, said in the dialog. */
  const [error, setError] = React.useState<string | null>(null);
  /** The plant's id, so a different plant starts again at the choices. */
  const plantObjId = plant?.plant_obj_id ?? null;

  React.useEffect(() => {
    setStep('choose');
    setError(null);
  }, [plantObjId]);

  /** The plants asked about together, or none. */
  const group = plant === null && many !== null && many.plants.length > 0 ? many : null;
  /** How many plants the question is about. */
  const total = group ? group.plants.length : 1;
  /** Photos and notes a delete takes: given for one plant, summed from the garden copies for several. */
  const counts = React.useMemo(() => {
    if (!group) return { photos, notes };
    const ids = new Set(group.plants.map((p) => p.plantObjId));
    let ph = 0;
    let nt = 0;
    (gardenList ?? []).forEach((p) => {
      if (!ids.has(p.plant_obj_id)) return;
      ph += p.photos?.length ?? 0;
      nt += p.notes?.length ?? 0;
    });
    return { photos: ph, notes: nt };
  }, [group, gardenList, photos, notes]);

  /** A step is on its way, so the dialog cannot be closed under it. */
  const busy = archiving || deleting;
  /** What the plant is called in the title and toasts: its one name (lib/plantName). */
  const name = plant ? plantName(plant) : '';

  /** Archives the plant, then says so in a short line that goes by itself (no Undo); a failure stays in the dialog. */
  const handleArchive = async (): Promise<void> => {
    if (group) {
      setArchiving(true);
      setError(null);
      try {
        await group.archiveAll(group.plants.map((p) => p.plantObjId));
        onClose();
        onDone?.();
      } catch (err) {
        setError(errorSentence(err, t('plants.form.removeFailed')));
      } finally {
        setArchiving(false);
      }
      return;
    }
    if (!plant) return;
    const id = plant.plant_obj_id;
    setArchiving(true);
    setError(null);
    try {
      await archive(id);
      onClose();
      onDone?.();
      // A plain line, gone after the toast's usual few seconds: no Undo and nothing that stays (B26).
      toast({ title: t('plants.form.removed', { name }) });
    } catch (err) {
      setError(errorSentence(err, t('plants.form.removeFailed')));
    } finally {
      setArchiving(false);
    }
  };

  /** Deletes the plant for good, archiving it first when it is still in the garden. */
  const handleDelete = async (): Promise<void> => {
    if (group) {
      setDeleting(true);
      setError(null);
      /** The plants that could not be deleted, each with why. */
      const failed: string[] = [];
      let gone = 0;
      for (const item of group.plants) {
        try {
          await group.archiveQuietly([item.plantObjId]);
          await deletePlantForever(item.plantObjId);
          gone += 1;
        } catch (err) {
          failed.push(item.name + ': ' + errorSentence(err, t('plants.archive.delete.failed')));
        }
      }
      try {
        await group.onDeleted?.();
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['plants'] }),
          queryClient.invalidateQueries({ queryKey: ['archived-plants'] }),
        ]);
      } finally {
        setDeleting(false);
      }
      if (gone > 0) toast({ title: t('plants.form.deletedMany', { count: gone }) });
      if (failed.length === 0) {
        onClose();
        onDone?.();
      } else {
        setError(failed.join('\n'));
      }
      return;
    }
    if (!plant) return;
    setDeleting(true);
    setError(null);
    try {
      // 0 is archived, 1 in the garden, as the server has it.
      if (plant.status !== 0) await archive(plant.plant_obj_id);
      await deletePlantForever(plant.plant_obj_id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['plants'] }),
        queryClient.invalidateQueries({ queryKey: ['archived-plants'] }),
      ]);
      onClose();
      onDone?.();
      toast({ title: t('plants.form.deleted', { name }) });
    } catch (err) {
      setError(errorSentence(err, t('plants.archive.delete.failed')));
      // The first step may have taken (archived, not deleted): read the lists again.
      void queryClient.invalidateQueries({ queryKey: ['plants'] });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={plant !== null || group !== null} onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent
        className={cn(
          'sm:max-w-sm',
          'max-sm:bottom-0 max-sm:left-0 max-sm:top-auto max-sm:max-h-[90dvh] max-sm:translate-x-0 max-sm:rounded-t-xl max-sm:border-x-0 max-sm:border-b-0 max-sm:px-4 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))] max-sm:pt-5',
          'max-sm:data-[state=open]:slide-in-from-bottom-8 max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:zoom-in-100'
        )}
      >
        {(plant || group) && step === 'delete' ? (
          <DeletePlantConfirm
            plantName={group ? group.plants[0].name : name}
            count={total}
            photos={counts.photos}
            notes={counts.notes}
            busy={deleting}
            error={error}
            onConfirm={() => { void handleDelete(); }}
            onCancel={() => { setError(null); setStep('choose'); }}
            renderTitle={(title) => (
              <DialogHeader className="-ml-2 flex-row items-center gap-1 space-y-0 text-left">
                <button
                  type="button"
                  onClick={() => { setError(null); setStep('choose'); }}
                  disabled={deleting}
                  aria-label={t('plants.form.back')}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <DialogTitle className="text-base">{title}</DialogTitle>
              </DialogHeader>
            )}
          />
        ) : (
          <>
            <DialogHeader className="text-left">
              <DialogTitle>
                {group && total > 1
                  ? t('plants.form.archiveOrDeleteTitleMany', { count: total })
                  : t('plants.form.archiveOrDeleteTitle', { name: group ? group.plants[0].name : name })}
              </DialogTitle>
            </DialogHeader>
            {group && (
              <ul aria-label={t('plants.form.manyList')} className="max-h-40 divide-y overflow-y-auto rounded-sm border text-sm">
                {group.plants.map((p) => (
                  <li key={p.plantObjId} className="break-words px-3 py-1.5">{p.name}</li>
                ))}
              </ul>
            )}
            <div className="space-y-2">
              {/* What Archive does, above the foot where its button sits. */}
              <p className="text-sm text-muted-foreground">{t(group && total > 1 ? 'plants.form.removeConfirmMany' : 'plants.form.removeConfirm')}</p>
              {/* Merge into…: only for a plant with twins (the form passes onMerge then); the sheet takes it from here. */}
              {plant && onMerge && (
                <Button variant="outline" onClick={() => { onClose(); onMerge(plant); }} disabled={busy} className="w-full">
                  {t('plants.merge.menu')}
                </Button>
              )}
              {/* Full-width and red; it only opens the type-DELETE question, so nothing is deleted by this tap. */}
              <Button
                variant="destructive"
                onClick={() => { setError(null); setStep('delete'); }}
                disabled={busy}
                className="w-full"
              >
                {group && total > 1 ? t('plants.form.deleteManyForever', { count: total }) : t('plants.form.deleteForeverEllipsis')}
              </Button>
              <p className="text-xs text-muted-foreground">{t(group && total > 1 ? 'plants.form.deleteHintMany' : 'plants.form.deleteHint')}</p>
              {error && <p className="whitespace-pre-line text-sm text-destructive"><FailureText text={error} /></p>}
            </div>
            <ActionRow onCancel={onClose} cancelLabel={t('plants.form.cancel')} disabled={busy}>
              <Button onClick={() => { void handleArchive(); }} loading={archiving} busy="archiving" disabled={busy}>
                {t('plants.form.remove')}
              </Button>
            </ActionRow>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
